import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BlackBackground = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/black-background',
			name: 'Black Background Maker — Add Black BG to Photo | Lift',
			description:
				'Add a black background to your photo instantly with AI. No watermark. Upload photo, create bold product images with strong contrast, and download in seconds. Try now!',
			url: 'https://liftapp.ai/black-background',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/black-background#product' },
				{ '@id': 'https://liftapp.ai/black-background#howto' },
				{ '@id': 'https://liftapp.ai/black-background#benefits' },
				{ '@id': 'https://liftapp.ai/black-background#faq' },
				{ '@id': 'https://liftapp.ai/black-background#why-lift' },
				{ '@id': 'https://liftapp.ai/black-background#explore-tools' },
				{ '@id': 'https://liftapp.ai/black-background#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/black-background#product',
			name: 'Black Background Maker Online',
			description:
				'Add a black background to any photo in seconds with AI. Get professional product images with bold contrast, no watermarks, batch up to 20 images.',
			url: 'https://liftapp.ai/black-background',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/black-background#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift adds a clean black background to your photo automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/black-background#benefits',
			name: 'Who can benefit from Black Background Maker?',
			description:
				"Lift's AI Black Background Maker is useful for professionals, creators, and businesses who want bold, high-contrast product images with black backgrounds for e-commerce, marketing, and visual content.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: "Online stores — Make your product listings stand out with bold contrast that catches shoppers' eyes instantly. Black backgrounds increase click-through rates and help your products pop in crowded marketplace search results.",
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Small business owners — Get studio-quality product photos without expensive equipment or photographers. Create professional black backdrop images in seconds and save time and money on photo shoots.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Marketers & SMM managers — Scale your visual content production for ads, catalogs, and campaigns with consistent black backgrounds. Process multiple product images at once and test different variations faster.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Content creators — Make your product content more eye-catching for Instagram, TikTok, and YouTube with high-contrast black backgrounds. No editing skills needed — just upload and download professional visuals instantly.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create polished presentations and project visuals with clean black backgrounds for maximum impact. Perfect for showcasing objects, designs, and visual materials with professional clarity.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Black image background',
				description:
					'Example of how Lift Black Background Maker creates bold black backgrounds for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/black-background#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Black Background Maker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Black Background Maker is a premium online tool that instantly adds a solid black backdrop to your photos with pixel-perfect precision — ideal for product photos, marketplace listings, or graphic design.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Black Background Maker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's black background tool. Our AI automatically detects the subject and adds a clean black backdrop in seconds — no photo editing experience needed. Download up to 20 images with professional black backgrounds.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Black Background Maker uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking photos without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/black-background#why-lift',
			name: "Why Lift's AI Black Background Maker stands out",
			description:
				"Lift's Black Background Maker combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Add black background to photo',
				description:
					"Comparison of Lift's AI Black Background Maker benefits — professional quality, AI accuracy, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/black-background#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/black-background#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Black Background Maker',
					item: 'https://liftapp.ai/black-background',
				},
			],
		},
	],
};
